import React, { ReactNode } from 'react';
import {
  createMonitoringReportContext,
  MonitoringReportProvider,
  type MonitoringReportContextType,
  type MonitoringReportAPIAdapter,
} from '../../shared';
import { ReportPhotoDTO } from '../../shared/photos/types';
import { LimitedReportState } from '../limited-report.type';
import { updateLimitedReport } from '../limited-report.api';
import { getFileUploadUrl } from '../../../project/project.api';
import {
  exportLimitedReportPdf,
  getPhotos as apiGetPhotos,
  getSignatureAsBase64,
  getImageAsBase64,
  uploadPhoto as apiUploadPhoto,
  batchUpdatePhotos as apiBatchUpdatePhotos,
  deletePhoto as apiDeletePhoto,
  updateEditedPhoto as apiUpdateEditedPhoto,
} from '../limited-report.api';
import { ReportExportUtil, type ExportReportData, type ExportSummary } from '../../shared/utils/export.util';

/**
 * Limited Report Context - uses generic MonitoringReportContext
 */
const LimitedReportContext = createMonitoringReportContext<ReportPhotoDTO, { limitedReportId?: string }>();

/**
 * Limited Report Context Type with backward compatibility
 */
export interface LimitedReportContextType extends Omit<
  MonitoringReportContextType<ReportPhotoDTO>,
  'reportState' | 'setReportState' | 'initialState'
> {
  reportState: LimitedReportState;
  setReportState: React.Dispatch<React.SetStateAction<LimitedReportState>>;
  initialState: LimitedReportState;
}

/**
 * Custom hook to use LimitedReport context
 * Returns properly typed context with LimitedReportState (includes limitedReportId)
 */
export const useLimitedReport = (): LimitedReportContextType => {
  const context = React.useContext(LimitedReportContext);
  if (!context) {
    throw new Error('useLimitedReport must be used within a LimitedReportProvider');
  }
  // Cast to LimitedReportContextType for backward compatibility with limitedReportId
  return context as unknown as LimitedReportContextType;
};

/**
 * Props for LimitedReportProvider
 */
interface LimitedReportProviderProps {
  children: ReactNode;
  initialData: LimitedReportState;
  projectId: string;
  limitedReportId: string;
}

/**
 * API adapter for Limited Reports
 */
const createLimitedReportAPI = (): MonitoringReportAPIAdapter<ReportPhotoDTO> => ({
  getPhotos: async (reportId: string) => {
    const photos = await apiGetPhotos(reportId);
    // Map limitedReportId to reportId for generic usage
    return photos.map(photo => ({
      ...photo,
      reportId: photo.reportId || reportId,
    }));
  },
  getPhotoUrl: (blobName: string) => getFileUploadUrl(blobName),
  updateReport: async (reportId: string, data: any) => {
    await updateLimitedReport(reportId, data);
  },
  buildReportHtml: async (state: any) => {
    // Build using shared ReportExportUtil with limited-report adapters
    const exporter = new ReportExportUtil(
      apiGetPhotos as any,
      getFileUploadUrl as any,
      getSignatureAsBase64 as any,
      async (photos: any[]) => {
        const { processPhotosForExport } = await import('../../shared/photos/utils');
        return processPhotosForExport(photos, getFileUploadUrl as any);
      },
    );

    const reportData: ExportReportData = {
      projectSummary: state.projectSummary,
      drawSummary: state.drawSummary,
      budgetSummary: state.budgetSummary,
      author: state.author,
      lineItems: state.lineItems,
      // Once Review Budget has edited the draw, its subtotals live on the (single) `draws` entry.
      subtotals: (state as any).draws?.[0]?.subtotals ?? state.subtotals,
      reportId: state.reportId,
      coverPagePhotoIds: state.coverPagePhotoIds || [],
      siteConditions: state.siteConditions,
    } as any;

    const summary: ExportSummary = {
      coverLetter: state.summary?.coverLetter,
      executiveSummary: state.summary?.executiveSummary,
      actionItems: state.summary?.actionItems,
    };

    return exporter.buildFullReportHtml(reportData, summary, 'limited');
  },
  buildPhotosOnlyHtml: async (reportId: string, options?: { projectName?: string; date?: Date }) => {
    const exporter = new ReportExportUtil(
      apiGetPhotos as any,
      getFileUploadUrl as any,
      getSignatureAsBase64 as any,
      async (photos: any[]) => {
        const { processPhotosForExport } = await import('../../shared/photos/utils');
        return processPhotosForExport(photos, getFileUploadUrl as any);
      },
    );
    return exporter.buildPhotosOnlyHtml(reportId, options);
  },
  exportPdf: exportLimitedReportPdf,
  uploadPhoto: apiUploadPhoto as any,
  batchUpdatePhotos: apiBatchUpdatePhotos as any,
  deletePhoto: apiDeletePhoto as any,
  updateEditedPhoto: apiUpdateEditedPhoto as any,
  getImageAsBase64: getImageAsBase64 as any,
});

/**
 * Limited Report Provider Component
 * Wraps the generic MonitoringReportProvider with Limited Report specific configuration
 */
export const LimitedReportProvider: React.FC<LimitedReportProviderProps> = ({ children, initialData, projectId, limitedReportId }) => {
  const api = React.useMemo(() => createLimitedReportAPI(), []);

  // Add backward compatibility by setting both reportId and limitedReportId
  const enrichedInitialData: LimitedReportState = {
    ...initialData,
    reportId: limitedReportId,
  };

  return (
    <MonitoringReportProvider
      context={LimitedReportContext}
      initialData={enrichedInitialData}
      projectId={projectId}
      reportId={limitedReportId}
      reportFieldName='limitedReportId' // Backend expects this field name for invoices
      reportType='limited'
      api={api}
    >
      {children}
    </MonitoringReportProvider>
  );
};

// Re-export types for backward compatibility
export type { UploadingPhoto } from '../../shared/photos/types';
export type { LimitedReportState } from '../limited-report.type';
